/**
 * Resolves subscription status including grace period transitions.
 * Used by client-side subscription helpers and mirrored in cloud functions.
 */

const DEFAULT_GRACE_PERIOD_DAYS = 7;

export function toSubscriptionDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getEffectiveEndDate(subscription) {
  if (!subscription) return null;

  if (subscription.status === 'cancelled') {
    return toSubscriptionDate(subscription.nextBillingDate) || toSubscriptionDate(subscription.endDate);
  }

  if (subscription.status === 'trial') {
    return toSubscriptionDate(subscription.nextBillingDate) || toSubscriptionDate(subscription.endDate);
  }

  return toSubscriptionDate(subscription.endDate) || toSubscriptionDate(subscription.nextBillingDate);
}

/**
 * @param {object} subscription
 * @param {number} [gracePeriodDays]
 * @param {Date} [now]
 */
export function resolveSubscriptionStatus(subscription, gracePeriodDays = DEFAULT_GRACE_PERIOD_DAYS, now = new Date()) {
  if (!subscription) {
    return {
      status: 'invalid',
      isValid: false,
      isInGracePeriod: false,
      isExpired: true,
      daysUntilExpiration: null,
      effectiveEndDate: null,
    };
  }

  if (subscription.isPermanent) {
    return {
      status: subscription.status || 'active',
      isValid: true,
      isInGracePeriod: false,
      isExpired: false,
      daysUntilExpiration: null,
      effectiveEndDate: null,
    };
  }

  if (subscription.status === 'trial') {
    const trialEnd = getEffectiveEndDate(subscription);
    if (!trialEnd) {
      return {
        status: 'trial',
        isValid: true,
        isInGracePeriod: false,
        isExpired: false,
        daysUntilExpiration: null,
        effectiveEndDate: null,
      };
    }

    const daysUntilExpiration = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    const isExpired = now > trialEnd;

    return {
      status: isExpired ? 'expired' : 'trial',
      isValid: !isExpired,
      isInGracePeriod: false,
      isExpired,
      daysUntilExpiration: isExpired ? 0 : daysUntilExpiration,
      effectiveEndDate: trialEnd,
    };
  }

  const effectiveEndDate = getEffectiveEndDate(subscription);
  if (!effectiveEndDate) {
    return {
      status: subscription.status || 'active',
      isValid: ['active', 'grace_period'].includes(subscription.status),
      isInGracePeriod: subscription.status === 'grace_period',
      isExpired: false,
      daysUntilExpiration: null,
      effectiveEndDate: null,
    };
  }

  const graceDays = Number.isFinite(Number(gracePeriodDays))
    ? Number(gracePeriodDays)
    : DEFAULT_GRACE_PERIOD_DAYS;
  const gracePeriodEnd = new Date(effectiveEndDate);
  gracePeriodEnd.setDate(gracePeriodEnd.getDate() + graceDays);

  const daysUntilExpiration = Math.ceil(
    (effectiveEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (subscription.status === 'cancelled') {
    const isExpired = now > effectiveEndDate;
    return {
      status: isExpired ? 'expired' : 'cancelled',
      isValid: !isExpired,
      isInGracePeriod: false,
      isExpired,
      daysUntilExpiration: isExpired ? 0 : daysUntilExpiration,
      effectiveEndDate,
    };
  }

  if (now <= effectiveEndDate) {
    return {
      status: 'active',
      isValid: true,
      isInGracePeriod: false,
      isExpired: false,
      daysUntilExpiration,
      effectiveEndDate,
    };
  }

  if (now > effectiveEndDate && now <= gracePeriodEnd) {
    return {
      status: 'grace_period',
      isValid: true,
      isInGracePeriod: true,
      isExpired: false,
      daysUntilExpiration: 0,
      effectiveEndDate,
    };
  }

  return {
    status: 'expired',
    isValid: false,
    isInGracePeriod: false,
    isExpired: true,
    daysUntilExpiration: 0,
    effectiveEndDate,
  };
}

/** Read nested subscription first, then legacy root fallback. */
export function getCompanySubscription(companyData) {
  if (!companyData) return null;
  return companyData.package?.subscription ?? companyData.subscription ?? null;
}

export function getGracePeriodDays(companyData, catalogPackage) {
  return (
    companyData?.package?.subscription?.gracePeriodDays ??
    catalogPackage?.subscription?.gracePeriodDays ??
    DEFAULT_GRACE_PERIOD_DAYS
  );
}
